import { v4 as uuidv4 } from 'uuid';
import { encryptContent, decryptContent, generateEncryptionKey } from '../utils/crypto';
import { compressContent, decompressContent, shouldCompress } from '../utils/compression';
import { supabase } from '../lib/supabase';
import { trackEvent, trackError } from '../utils/analytics';
import { addCsrfHeader } from '../utils/csrf';
import type { CreateNoteDto, Note, NoteCreationResult, NoteAutopass } from '../types';

export const createNote = async (dto: CreateNoteDto): Promise<NoteCreationResult> => {
  try {
    const noteId = uuidv4();
    
    // Generate a unique encryption key for non-password protected notes
    const encryptionKey = dto.password || generateEncryptionKey();
    
    // Check if content should be compressed
    const shouldBeCompressed = shouldCompress(dto.content);
    const processedContent = shouldBeCompressed 
      ? compressContent(dto.content.trim())
      : dto.content.trim();
    
    const encrypted = await encryptContent(processedContent, encryptionKey);
    
    // Set expiration to end of the day X days from now
    const expiresat = new Date();
    expiresat.setDate(expiresat.getDate() + dto.expiresInDays);
    expiresat.setHours(23, 59, 59, 999);

    const { data: note, error } = await supabase
      .from('notes')
      .insert({
        id: noteId,
        content: encrypted,
        encrypted: !!dto.password,
        expiresat: expiresat.toISOString(),
        expired: false,
        view_limit: dto.viewLimit,
        view_count: 0,
        compressed: shouldBeCompressed
      }, {
        headers: addCsrfHeader({})
      })
      .select('id, short_id')
      .single();

    if (error) {
      console.error('Note creation error:', error);
      if (error.message.includes('check_expiration_date')) {
        throw new Error('Note expiration date must be between 1 and 30 days from creation');
      }
      if (error.message.includes('check_view_limit')) {
        throw new Error('View limit must be a positive number');
      }
      throw new Error(error.message);
    }

    let autopassToken: string | undefined;

    // For non-password protected notes, store the encryption key in notes_autopass
    if (!dto.password) {
      const { data: autopass, error: autopassError } = await supabase
        .from('notes_autopass')
        .insert({
          id: noteId,
          token: encryptionKey // Store the encryption key as the token
        }, {
          headers: addCsrfHeader({})
        })
        .select('token')
        .single();

      if (autopassError) {
        throw new Error('Failed to generate autopass token');
      }

      autopassToken = autopass.token;
    }

    trackEvent('note_created', {
      is_encrypted: !!dto.password,
      expires_in_days: dto.expiresInDays,
      has_view_limit: !!dto.viewLimit,
      has_autopass: !dto.password,
      is_compressed: shouldBeCompressed
    });

    return { 
      id: noteId,
      short_id: note.short_id,
      url: `/note/${note.short_id}`,
      autopassToken 
    };
  } catch (error) {
    console.error('Note creation error:', error);
    trackError(error instanceof Error ? error : new Error('Note creation failed'), 'createNote');
    throw error;
  }
};

export const getNote = async (shortId: string): Promise<Note> => {
  try {
    const { data: { user_ip } } = await supabase.rpc('get_client_ip', undefined, {
      headers: addCsrfHeader({})
    });
    
    if (!user_ip) {
      throw new Error('Could not determine client IP');
    }

    // Try to get note by short_id first
    let { data, error } = await supabase
      .from('notes')
      .select()
      .eq('short_id', shortId)
      .maybeSingle();

    // If not found by short_id, try by UUID
    if (!data && !error) {
      ({ data, error } = await supabase
        .from('notes')
        .select()
        .eq('id', shortId)
        .maybeSingle());
    }

    if (error || !data) {
      const { data: rateLimits } = await supabase
        .from('rate_limits')
        .select()
        .eq('ip', user_ip)
        .eq('note_id', shortId)
        .maybeSingle();

      const attempts = (rateLimits?.attempts || 0) + 1;
      const blocked = attempts >= 5;
      const blockeduntil = blocked 
        ? new Date(Date.now() + 30 * 60 * 1000).toISOString()
        : null;

      await supabase
        .from('rate_limits')
        .upsert({
          ip: user_ip,
          note_id: shortId,
          attempts,
          blocked,
          blocked_until: blockeduntil,
          last_attempt: new Date().toISOString()
        }, {
          headers: addCsrfHeader({})
        });

      if (blocked) {
        throw new Error('Access blocked due to too many failed attempts. Please try again in 30 minutes.');
      }
      throw new Error('Note not found');
    }

    trackEvent('note_accessed', {
      is_encrypted: data.encrypted,
      is_expired: data.expired,
      has_view_limit: !!data.view_limit,
      view_count: data.view_count,
      is_compressed: data.compressed
    });

    return data as Note;
  } catch (error) {
    console.error('Note retrieval error:', error);
    trackError(error instanceof Error ? error : new Error('Note retrieval failed'), 'getNote');
    throw error;
  }
};

interface AccessNoteResult {
  content?: string;
  error?: {
    message: string;
    isInvalidPassword?: boolean;
  };
}

export const accessNote = async (
  shortId: string,
  password: string,
  autopassToken?: string
): Promise<AccessNoteResult> => {
  try {
    // Try to get note by short_id first
    let { data: note, error } = await supabase
      .from('notes')
      .select()
      .eq('short_id', shortId)
      .maybeSingle();

    // If not found by short_id, try by UUID
    if (!note && !error) {
      ({ data: note, error } = await supabase
        .from('notes')
        .select()
        .eq('id', shortId)
        .maybeSingle());
    }

    if (error || !note) {
      return { error: { message: 'Note not found' } };
    }

    if (note.expired) {
      trackEvent('note_access_blocked', { reason: 'already_expired' });
      return { error: { message: 'This note has already been viewed and is no longer accessible' } };
    }

    if (note.view_limit !== null && note.view_count >= note.view_limit) {
      trackEvent('note_access_blocked', { reason: 'view_limit_reached' });
      return { error: { message: 'This note has reached its maximum number of views' } };
    }

    let decryptionKey = password;

    // For non-password protected notes, get the encryption key from notes_autopass
    if (!note.encrypted) {
      const { data: autopass, error: autopassError } = await supabase
        .from('notes_autopass')
        .select('token')
        .eq('id', note.id)
        .single();

      if (autopassError || !autopass) {
        return { error: { message: 'Invalid or expired autopass token' } };
      }

      decryptionKey = autopass.token;
    }

    try {
      const decrypted = await decryptContent(note.content, decryptionKey);
      
      // Decompress content if it was compressed
      const finalContent = note.compressed 
        ? decompressContent(decrypted)
        : decrypted;
      
      const { error: updateError } = await supabase
        .rpc('increment_note_views', { note_id: note.id }, {
          headers: addCsrfHeader({})
        });

      if (updateError) {
        trackError(updateError, 'incrementNoteViews');
      }

      // On successful decryption, delete rate limit record
      const { data: { user_ip } } = await supabase.rpc('get_client_ip');
      if (user_ip) {
        await supabase
          .rpc('delete_rate_limit', { 
            user_ip: user_ip,
            note_uuid: note.id
          }, {
            headers: addCsrfHeader({})
          });
      }

      trackEvent('note_decrypted_success', {
        is_encrypted: note.encrypted,
        has_view_limit: !!note.view_limit,
        view_count: note.view_count + 1,
        is_compressed: note.compressed
      });
      
      return { content: finalContent };
    } catch (error) {
      console.log('Decryption failed, checking rate limits');
      
      const { data: { user_ip, rate_limit_config } } = await supabase.rpc('get_client_ip');
      
      if (!user_ip) {
        return { error: { message: 'Could not determine client IP' } };
      }

      const { data: rateLimits } = await supabase
        .from('rate_limits')
        .select()
        .eq('ip', user_ip)
        .eq('note_id', note.id)
        .maybeSingle();

      const config = rate_limit_config as { block_duration_minutes: number; max_attempts: number };
      const attempts = (rateLimits?.attempts || 0) + 1;
      const blocked = attempts >= config.max_attempts;
      const blockeduntil = blocked 
        ? new Date(Date.now() + config.block_duration_minutes * 60 * 1000).toISOString()
        : null;

      console.log('Updating rate limits:', {
        ip: user_ip,
        note_id: note.id,
        attempts,
        blocked,
        blockeduntil
      });

      const { error: updateError } = await supabase
        .from('rate_limits')
        .upsert({
          ip: user_ip,
          note_id: note.id,
          attempts,
          blocked,
          blocked_until: blockeduntil,
          last_attempt: new Date().toISOString()
        }, {
          headers: addCsrfHeader({})
        });

      if (updateError) {
        console.error('Failed to update rate limits:', updateError);
      }

      trackEvent('note_decryption_failed', { attempts, blocked });

      if (blocked) {
        return { 
          error: { 
            message: `Access blocked due to too many failed attempts. Please try again in ${config.block_duration_minutes} minutes.` 
          } 
        };
      }
      
      return { 
        error: { 
          message: 'Invalid password',
          isInvalidPassword: true
        } 
      };
    }
  } catch (error) {
    console.error('Note access error:', error);
    trackError(error instanceof Error ? error : new Error('Note access failed'), 'accessNote');
    return { 
      error: { 
        message: error instanceof Error ? error.message : 'Failed to access note'
      } 
    };
  }
};